/**
 * DiscoverEducators — recruiter-facing search.
 * Returns permission-masked teacher cards; recruiter can request CV.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/Skeleton";
import {
  Search, MapPin, GraduationCap, Briefcase, Filter, Sparkles, Shield,
  Eye, Star, ArrowRight, Globe, BookOpen, Loader2, FileText,
} from "lucide-react";
import { toast } from "sonner";

export default function DiscoverEducators() {
  const navigate = useNavigate();
  const [facets, setFacets] = useState(null);
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    q: "", category: "", is_extra_curricular: null, mode: "", experience_min: "",
    location: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/marketplace/facets").then((r) => setFacets(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    runSearch();
    // eslint-disable-next-line
  }, []);

  const runSearch = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== "" && v !== null && v !== undefined)
      );
      const { data } = await api.get("/marketplace/educators", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Discover Educators">
      <div className="mb-5">
        <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
          <Briefcase className="h-3 w-3" /> TeeBee Educator Marketplace
        </span>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
          Discover educators
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-xl">
          {total > 0 ? `${total} educators` : "Find teachers"} across curricular and extra-curricular categories. You&apos;ll only see fields they&apos;ve allowed.
        </p>
      </div>

      {/* Filter bar */}
      <section className="tb-card p-4 mb-5 grid grid-cols-1 md:grid-cols-12 gap-2.5" data-testid="discover-filters">
        <div className="md:col-span-4 relative">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            className="tb-input pl-9"
            placeholder="Search by name, subject, headline..."
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            data-testid="discover-q"
          />
        </div>
        <select className="tb-input md:col-span-2" value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })} data-testid="discover-category">
          <option value="">All categories</option>
          {facets && [...facets.curricular_categories, "—", ...facets.extra_curricular_categories].map((c) => (
            c === "—" ? <option key="sep" disabled>──────────</option> : <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select className="tb-input md:col-span-2" value={filters.is_extra_curricular === null ? "" : String(filters.is_extra_curricular)}
                onChange={(e) => setFilters({ ...filters, is_extra_curricular: e.target.value === "" ? null : e.target.value === "true" })}
                data-testid="discover-type">
          <option value="">Curricular & extra</option>
          <option value="false">Curricular only</option>
          <option value="true">Extra-curricular only</option>
        </select>
        <select className="tb-input md:col-span-1" value={filters.mode}
                onChange={(e) => setFilters({ ...filters, mode: e.target.value })} data-testid="discover-mode">
          <option value="">Any mode</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
          <option value="hybrid">Hybrid</option>
        </select>
        <input className="tb-input md:col-span-1" type="number" min={0} placeholder="Exp ≥" value={filters.experience_min}
               onChange={(e) => setFilters({ ...filters, experience_min: e.target.value })} data-testid="discover-exp" />
        <input className="tb-input md:col-span-1" placeholder="Location" value={filters.location}
               onChange={(e) => setFilters({ ...filters, location: e.target.value })} data-testid="discover-location" />
        <Button onClick={runSearch} disabled={loading} className="rounded-full md:col-span-1" data-testid="discover-search-btn">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Filter className="h-3.5 w-3.5" />}
        </Button>
      </section>

      {/* Results */}
      {!items ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-44" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="tb-card p-10 text-center" data-testid="discover-empty">
          <span className="mx-auto h-14 w-14 grid place-items-center rounded-full border border-dashed border-border mb-3">
            <Search className="h-6 w-6 text-muted-foreground" />
          </span>
          <p className="font-display text-lg font-medium">No educators match yet</p>
          <p className="mt-1 text-sm text-muted-foreground max-w-sm mx-auto">
            Try widening the category, removing the experience filter, or check back as more teachers turn on Marketplace.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 tb-stagger">
          {items.map((it) => <EducatorCard key={it.user_id} edu={it} navigate={navigate} />)}
        </div>
      )}
    </Layout>
  );
}

function EducatorCard({ edu, navigate }) {
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);

  const requestCV = async (e) => {
    e.stopPropagation();
    setRequesting(true);
    try {
      await api.post("/marketplace/cv-request", { teacher_id: edu.user_id, message: "Interested in your profile via TeeBee Marketplace." });
      setRequested(true);
      toast.success("CV access requested", { description: "The teacher will respond shortly." });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send request");
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div
      onClick={() => navigate(`/hiring/educator/${edu.user_id}`)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/hiring/educator/${edu.user_id}`); } }}
      role="button"
      tabIndex={0}
      className="tb-card-interactive p-4 text-left flex flex-col cursor-pointer" data-testid={`educator-card-${edu.user_id}`}
    >
      <div className="flex items-center gap-3 mb-2">
        <span className="h-10 w-10 rounded-2xl tb-chip-lavender grid place-items-center font-display text-sm font-medium">
          {(edu.name || "E").slice(0, 1)}
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-display text-base font-medium tracking-tight line-clamp-1">{edu.name}</p>
          <p className="text-[11px] text-muted-foreground line-clamp-1">{edu.headline || "Educator"}</p>
        </div>
        {edu.verified && <Star className="h-3.5 w-3.5 text-amber-500" aria-label="Verified" />}
      </div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {(edu.teaching_categories || []).slice(0, 3).map((c) => (
          <span key={c} className="tb-chip-sky rounded-full text-[10px] px-2 py-0.5">{c}</span>
        ))}
      </div>
      <ul className="text-[12px] text-muted-foreground space-y-1 mb-3">
        {edu.experience_years != null && <li className="flex items-center gap-1.5"><Briefcase className="h-3 w-3" /> {edu.experience_years} yrs experience</li>}
        {edu.preferred_location && <li className="flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {edu.preferred_location}</li>}
        {edu.modes?.length > 0 && <li className="flex items-center gap-1.5"><Globe className="h-3 w-3" /> {edu.modes.join(" · ")}</li>}
        {edu.salary_band && <li className="flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> {edu.salary_band}</li>}
      </ul>
      <div className="mt-auto flex items-center justify-between gap-2 pt-2 border-t border-border/60">
        <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground inline-flex items-center gap-1">
          <Shield className="h-3 w-3" /> {edu.profile_strength || 0}/100
        </span>
        <Button
          size="sm"
          variant={requested ? "outline" : "default"}
          className="rounded-full"
          disabled={requesting || requested}
          onClick={requestCV}
          data-testid={`educator-request-cv-${edu.user_id}`}
        >
          {requesting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileText className="h-3 w-3 mr-1" />}
          {requested ? "Requested" : "Request CV"}
        </Button>
      </div>
    </div>
  );
}
